/**
 * Certified invoicing's gate before a checkout (docs/BILLING-CERTIFIED-INVOICING-JASMIN.md
 * §4.1), for both portals: every paid checkout first asks the server whether
 * the invoice it will produce has a complete recipient. If not, the portal
 * opens its billing-details form, and the checkout continues the moment the
 * details are saved — the payer never has to start the purchase again.
 *
 * Whether anything is missing, and whether the gate is on at all, is the
 * server's answer (`GET /billing/fiscal/readiness`); this holds the purchase
 * while the details are filled in. A refusal that still reaches a checkout
 * (`BILLING_PROFILE_INCOMPLETE` — the details changed in between) opens the
 * same form, through `refused`.
 */
import { useCallback, useRef, useState } from 'react';

import { useCheckFiscalReadiness } from './useBilling';

type Proceed = () => void | Promise<void>;

export interface FiscalCheckoutGate {
  /** Run `proceed` now if the details are complete, else once they are saved. */
  beforeCheckout: (proceed: Proceed) => Promise<void>;
  /** A checkout the server refused for the details: ask for them, then run `proceed`. */
  refused: (proceed: Proceed) => void;
}

/** What the portal's billing-details dialog is driven by. */
export interface FiscalCheckoutGateDialog {
  open: boolean;
  onSaved: () => void;
  onOpenChange: (open: boolean) => void;
}

export function useFiscalCheckoutGate(): {
  gate: FiscalCheckoutGate;
  dialog: FiscalCheckoutGateDialog;
} {
  const checkReadiness = useCheckFiscalReadiness();
  const held = useRef<Proceed | null>(null);
  const [open, setOpen] = useState(false);

  const beforeCheckout = useCallback(
    async (proceed: Proceed) => {
      const readiness = await checkReadiness();
      if (readiness.required && readiness.missing.length > 0) {
        held.current = proceed;
        setOpen(true);
        return;
      }
      await proceed();
    },
    [checkReadiness]
  );

  const refused = useCallback((proceed: Proceed) => {
    held.current = proceed;
    setOpen(true);
  }, []);

  const onSaved = useCallback(() => {
    const proceed = held.current;
    held.current = null;
    setOpen(false);
    void proceed?.();
  }, []);

  const onOpenChange = useCallback((next: boolean) => {
    if (next) return;
    held.current = null;
    setOpen(false);
  }, []);

  return { gate: { beforeCheckout, refused }, dialog: { open, onSaved, onOpenChange } };
}
